import { inject, Injectable } from '@angular/core'

import {
  area as getArea,
  bbox as getBbox,
  booleanPointInPolygon,
  length as getLength,
  lineString,
  point as toPoint,
  pointToLineDistance,
} from '@turf/turf'
import { BBox, Feature, LineString } from 'geojson'
import { differenceBy, intersectionBy } from 'lodash-es'
import RBush from 'rbush'
import { BehaviorSubject, Observable } from 'rxjs'

import { Location, Polygon } from '@ti/contracts'
import { Coordinates } from '@ti/shared/map/contracts'
import { toLngLat } from '@ti/shared/map/utils'
import { storedCorridorWidthToMeters } from '@ti/shared/map/utils/corridor'

import { getCorridorBbox } from '../utils/corridor-bbox'
import { LocationsStore } from './locations.store'

interface RBushItem {
  minX: number
  minY: number
  maxX: number
  maxY: number
}

interface IndexEntry extends RBushItem {
  id: string
  area: number
  bbox: BBox
  location: Location
  corridor?: { centerline: Feature<LineString>; halfWidthMeters: number }
}

@Injectable({ providedIn: 'root' })
export class LocationsIndex {
  public readonly refreshed$: Observable<void>
  protected readonly rTreeIdx = new RBush<IndexEntry>()
  protected readonly locations = inject(LocationsStore)
  protected readonly matchResultCache = new Map<string, Location[]>()
  protected readonly emitIdxRefreshed$ = new BehaviorSubject<void>(undefined)

  public constructor() {
    Promise.resolve()
      .then(() => this.init())
      .catch((error) => console.error(`Cannot initialize LocationsIndex`, error))

    this.refreshed$ = this.emitIdxRefreshed$.asObservable()
  }

  public match(latLng: Coordinates): Location[] | undefined {
    const cacheKey = `${latLng[0].toFixed(5)}_${latLng[1].toFixed(5)}`
    if (!this.matchResultCache.has(cacheKey)) {
      const candidates = this.rTreeIdx.search({
        minX: latLng[1],
        minY: latLng[0],
        maxX: latLng[1] + 0.00001,
        maxY: latLng[0] + 0.00001,
      })

      if (candidates.length) {
        const point = toPoint(toLngLat(latLng))
        const matches = candidates.filter((candidate) => {
          try {
            if (candidate.corridor) {
              const { centerline, halfWidthMeters } = candidate.corridor
              return pointToLineDistance(point, centerline, { units: 'meters' }) <= halfWidthMeters
            }
            return booleanPointInPolygon(point, candidate.location.geolocation as Polygon)
          } catch (e) {
            console.error(`Cannot match geofence`, e, { point, candidate })
          }
        })

        // Sort locations ASC by area
        matches.sort((a, b) => a.area - b.area)

        // Return location with the smallest area
        this.matchResultCache.set(
          cacheKey,
          matches.map((m) => m.location),
        )
      }
    }

    return this.matchResultCache.get(cacheKey)
  }

  protected init() {
    let prevLocations: Location[] = []
    this.locations.state$.items.subscribe((locations) => {
      const updatedLocations = intersectionBy(locations, prevLocations, 'id')
      const addedLocations = differenceBy(locations, updatedLocations, 'id')
      const deletedLocations = differenceBy(prevLocations, updatedLocations, 'id')

      if (addedLocations.length) {
        this.rTreeIdx.load(addedLocations.map((data) => this.locationToIndexEntry(data)))
      }

      if (updatedLocations.length) {
        updatedLocations.forEach((location) => {
          const prevLocation = prevLocations.find((item) => item.id === location.id) as Location
          if (prevLocation.updatedAt?.valueOf() !== location.updatedAt?.valueOf()) {
            // TODO: Compare old and new bbox before updating the index data
            const indexData = this.locationToIndexEntry(location)
            this.rTreeIdx.remove(indexData, (a, b) => a.id === b.id)
            this.rTreeIdx.insert(indexData)
          }
        })
      }

      if (deletedLocations.length) {
        deletedLocations.forEach((location) => {
          const indexData = this.rTreeIdx.all().find((item) => item.id === location.id)
          if (indexData) {
            this.rTreeIdx.remove(indexData, (a, b) => a.id === b.id)
          }
        })
      }

      prevLocations = locations
      this.matchResultCache.clear()
      this.emitIdxRefreshed$.next()
    })
  }

  protected locationToIndexEntry(location: Location): IndexEntry {
    if (location.geolocation.type === 'LineString') {
      return this.corridorToIndexEntry(location, location.geolocation)
    }

    const area = getArea(location.geolocation)
    const bbox = getBbox(location.geolocation)
    return {
      id: location.id,
      minX: bbox[0],
      minY: bbox[1],
      maxX: bbox[2],
      maxY: bbox[3],
      area,
      bbox,
      location,
    }
  }

  protected corridorToIndexEntry(location: Location, geolocation: LineString): IndexEntry {
    const centerline = lineString(geolocation.coordinates)
    const halfWidthMeters = storedCorridorWidthToMeters(location.properties?.width ?? 0) / 2
    const bbox = getCorridorBbox(geolocation.coordinates, halfWidthMeters)
    // The band's area in m², so corridors sort by size among polygons instead of first with zero
    const area =
      getLength(centerline, { units: 'meters' }) * halfWidthMeters * 2 +
      Math.PI * halfWidthMeters ** 2

    return {
      id: location.id,
      minX: bbox[0],
      minY: bbox[1],
      maxX: bbox[2],
      maxY: bbox[3],
      area,
      bbox,
      location,
      corridor: { centerline, halfWidthMeters },
    }
  }
}
